import { Fragment, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BotAction } from '../types';
import type { ActionTestResult } from '../api';
import { DataTable, Cell, Empty } from '../table';
import { Confirm } from './Confirm';
import { TryAction } from './TryAction';

/** What is saved for one action. The secret is optional: absent, the stored one stays. */
export interface ActionDraft {
  actionKey: string; name?: string; description?: string; method?: string; url: string;
  headers?: Record<string, string>; authType?: string | null; authHeader?: string | null;
  secret?: string;
}

/**
 * The calls this action panel makes.
 *
 * Injected for the same reason the editor's is: PSM's console addresses a client's
 * actions through /admin/tenants/{id}, a client's own portal through routes the
 * server scopes for them. One panel, two addresses.
 */
export interface ActionStudioApi {
  save: (action: ActionDraft) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
  /** Calls the action once, through the same code a flow would. Absent, there is no Test. */
  test?: (id: number, body: { variables?: Record<string, unknown>; body?: unknown; live?: boolean }) => Promise<ActionTestResult>;
}

const FIELD = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body focus-visible:outline-none focus-visible:border-(--color-brand)';
const BUTTON = 'rounded-lg border border-(--color-line) px-3 py-1.5 text-small transition hover:bg-(--color-sunken) disabled:opacity-40';
const PRIMARY = 'rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) transition hover:bg-(--color-brand-strong) disabled:opacity-40';

interface Form {
  actionKey: string; name: string; description: string; method: string; url: string;
  headers: [string, string][]; authType: string; authHeader: string; secret: string;
}

const EMPTY: Form = { actionKey: '', name: '', description: '', method: 'POST', url: '', headers: [], authType: 'none', authHeader: '', secret: '' };

function formOf(action: BotAction): Form {
  const config = safeConfig(action.config);
  return {
    actionKey: action.actionKey,
    name: action.name ?? '',
    description: action.description ?? '',
    method: config.method ?? 'POST',
    url: config.url ?? '',
    headers: Object.entries(config.headers ?? {}),
    authType: action.authType ?? 'none',
    authHeader: action.authHeader ?? '',
    secret: '',
  };
}

/**
 * The named calls a flow may make.
 *
 * A flow references one of these by key and never sees the URL or the credential —
 * which is not tidiness but the entire SSRF answer: a flow that cannot express a URL
 * cannot be pointed at an internal address.
 */
export function ActionsPanel({ actions, api, readOnly = false }: {
  actions: BotAction[];
  api: ActionStudioApi;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Form | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [testing, setTesting] = useState<number | null>(null);
  const [retiring, setRetiring] = useState<BotAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Retiring used to be fire-and-forget, and the failure was silent in the worst
  // possible way: a live bot kept running and started taking its "your system is
  // down" branch on every conversation. The server refuses that now; this is the
  // half that puts the refusal in front of the person who clicked.
  const retire = async (id: number) => {
    setError(null);
    setNote(null);
    try {
      const result = await api.remove(id) as { usedBy?: string[] } | null;
      if (result?.usedBy?.length) {
        setNote(`Retired. These drafts call it and will stop at that step: ${result.usedBy.join(', ')}.`);
      }
      await queryClient.invalidateQueries({ queryKey: ['bot-actions'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not retire that action.');
    }
  };

  const save = useMutation({
    mutationFn: (f: Form) => api.save({
      actionKey: f.actionKey.trim(),
      name: f.name.trim() || f.actionKey.trim(),
      description: f.description.trim() || undefined,
      method: f.method,
      url: f.url.trim(),
      headers: Object.fromEntries(f.headers.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v])),
      authType: f.authType === 'none' ? null : f.authType,
      authHeader: f.authHeader.trim() || null,
      secret: f.secret || undefined,
    }),
    onSuccess: () => {
      setError(null);
      setForm(null);
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['bot-actions'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save that action.'),
  });

  const startEdit = (action: BotAction) => { setEditing(action.id); setForm(formOf(action)); setError(null); };

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-lead font-semibold">Actions</h2>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => { if (form) { setForm(null); setEditing(null); } else { setForm(EMPTY); setEditing(null); } }}
          className={`${BUTTON} ml-auto px-3.5 py-2 text-body`}
        >
          {form ? 'Cancel' : 'Add an action'}
        </button>
      </div>

      <p className="mb-3 max-w-[90ch] text-small leading-relaxed text-(--color-muted)">
        A flow names one of these; it never carries a URL or a credential of its own.
        That is deliberate &mdash; a flow that cannot express an address cannot be
        pointed at an internal one, and a flow exported or pasted into a chat carries
        no secrets with it.
      </p>

      {error && (
        <p role="alert" className="mb-2 rounded-lg border border-(--color-bad) bg-(--color-bad-wash) px-4 py-3 text-body text-(--color-bad)">
          {error}
        </p>
      )}

      {note && (
        <p className="mb-2 rounded-lg border border-(--color-warn) bg-(--color-warn-wash) px-4 py-3 text-body text-(--color-ink-soft)">
          {note}
        </p>
      )}

      {form && (
        <ActionForm
          form={form}
          editing={editing !== null}
          busy={save.isPending}
          onChange={setForm}
          onSubmit={() => save.mutate(form)}
          onCancel={() => { setForm(null); setEditing(null); }}
        />
      )}

      {actions.length === 0 ? (
        <Empty>No actions yet. A flow needs one before it can call anything.</Empty>
      ) : (
        <>
          {/* Same reason as the flows list: the buttons are the last column of a
              five-column table, so on a phone they sit off the right inside the
              scroller where a layout check does not look. Cards below sm. */}
          <ul className="flex flex-col gap-2 sm:hidden">
            {actions.map((action) => {
              const config = safeConfig(action.config);
              return (
                <li key={action.id} className="rounded-lg border border-(--color-line) bg-(--color-surface) p-4">
                  <p className="font-mono text-small text-(--color-muted)">{action.actionKey}</p>
                  <p className="text-lead font-medium">{action.name}</p>
                  {action.description && <p className="mt-0.5 text-small text-(--color-muted)">{action.description}</p>}
                  <p className="mt-1.5 font-mono text-small break-all text-(--color-muted)">
                    {config.method} {config.url}
                  </p>
                  <p className="mt-1.5 text-small text-(--color-muted)">
                    {action.hasSecret ? `${action.authType} · credential stored` : 'no credential'}
                  </p>
                  {!readOnly && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {api.test && <button type="button" onClick={() => setTesting(testing === action.id ? null : action.id)} className={`${BUTTON} py-2`}>Test</button>}
                      <button type="button" onClick={() => startEdit(action)} className={`${BUTTON} py-2`}>Edit</button>
                      <button type="button" onClick={() => setRetiring(action)} className={`${BUTTON} py-2 text-(--color-muted)`}>Retire</button>
                    </div>
                  )}
                  {testing === action.id && api.test && <div className="mt-3 rounded-lg border border-(--color-line) bg-(--color-paper) p-3"><TryAction action={action} test={api.test} /></div>}
                </li>
              );
            })}
          </ul>

          <div className="hidden sm:block">
            <DataTable head={['Key', 'Name', 'Call', 'Credential', '']}>
              {actions.map((action) => {
                const config = safeConfig(action.config);
                return (
                  <Fragment key={action.id}>
                    <tr className="border-t border-(--color-line-dim)">
                      <Cell mono nowrap>{action.actionKey}</Cell>
                      <Cell>
                        {action.name}
                        {action.description && <span className="block text-small text-(--color-muted)">{action.description}</span>}
                      </Cell>
                      <Cell mono>
                        {/* The URL is one long unbreakable token, so without this it
                            claims the width and squeezes every other column. */}
                        <span className="break-all">{config.method} {config.url}</span>
                      </Cell>
                      <Cell nowrap>{action.hasSecret ? `${action.authType} · stored` : 'none'}</Cell>
                      <Cell nowrap>
                        {!readOnly && (
                          <span className="flex justify-end gap-1.5">
                            {api.test && (
                              <button type="button" onClick={() => setTesting(testing === action.id ? null : action.id)} className={BUTTON} aria-expanded={testing === action.id}>
                                Test
                              </button>
                            )}
                            <button type="button" onClick={() => startEdit(action)} className={BUTTON}>Edit</button>
                            <button type="button" onClick={() => setRetiring(action)} className={`${BUTTON} text-(--color-muted)`}>Retire</button>
                          </span>
                        )}
                      </Cell>
                    </tr>
                    {testing === action.id && api.test && (
                      <tr>
                        <td colSpan={5} className="bg-(--color-paper) px-4 pb-4 pt-1">
                          <TryAction action={action} test={api.test} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </DataTable>
          </div>
        </>
      )}

      {retiring && (
        <Confirm
          title={`Retire ${retiring.actionKey}?`}
          body="Flows that name it will no longer be able to call it. The server refuses if a live bot depends on it."
          confirm="Retire"
          danger
          onCancel={() => setRetiring(null)}
          onConfirm={() => { const id = retiring.id; setRetiring(null); void retire(id); }}
        />
      )}
    </section>
  );
}

const slug = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

function ActionForm({ form, editing, busy, onChange, onSubmit, onCancel }: {
  form: Form; editing: boolean; busy: boolean;
  onChange: (next: Form) => void; onSubmit: () => void; onCancel: () => void;
}) {
  // The key follows the name until somebody types a key of their own. "crm_lookup"
  // is a thing flows refer to, not a thing a person should have to invent first.
  const [keyTouched, setKeyTouched] = useState(editing || form.actionKey !== slug(form.name));
  const set = (patch: Partial<Form>) => onChange({ ...form, ...patch });
  const setName = (name: string) => onChange({ ...form, name, actionKey: keyTouched ? form.actionKey : slug(name) });
  const https = /^https:\/\/\S+$/i.test(form.url.trim());
  const placeholders = [...form.url.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map((m) => m[1]);

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="mb-3 grid gap-3 rounded-lg border border-(--color-line) bg-(--color-surface) p-4 sm:grid-cols-2"
    >
      <p className="text-body font-medium sm:col-span-2">{editing ? `Editing ${form.actionKey}` : 'A new action'}</p>

      <label className="block">
        <span className="text-small font-medium text-(--color-muted)">What it does</span>
        <input value={form.name} onChange={(e) => setName(e.target.value)} className={FIELD} placeholder="Look up a customer by mobile" autoFocus={!editing} />
        {!editing && (
          <span className="mt-1 block text-small text-(--color-faint)">
            Flows will refer to it as{' '}
            <input
              value={form.actionKey}
              onChange={(e) => { setKeyTouched(true); set({ actionKey: e.target.value.replace(/\s+/g, '_') }); }}
              aria-label="Key flows use"
              className="inline-block w-72 max-w-full rounded border border-transparent bg-transparent px-1 font-mono text-(--color-ink) hover:border-(--color-line) focus-visible:border-(--color-brand) focus-visible:outline-none"
            />
          </span>
        )}
      </label>

      <label className="block">
        <span className="text-small font-medium text-(--color-muted)">Method</span>
        <select value={form.method} onChange={(e) => set({ method: e.target.value })} className={FIELD}>
          <option value="GET">GET — read something</option>
          <option value="POST">POST — create or send something</option>
          <option value="PUT">PUT — replace something</option>
          <option value="PATCH">PATCH — change part of something</option>
          <option value="DELETE">DELETE — remove something</option>
        </select>
      </label>

      <label className="block sm:col-span-2">
        <span className="text-small font-medium text-(--color-muted)">Address (https only)</span>
        <input value={form.url} onChange={(e) => set({ url: e.target.value })} className={`${FIELD} font-mono ${form.url && !https ? 'border-(--color-bad)' : ''}`} placeholder="https://crm.example.com/customers?mobile={{mobile}}" spellCheck={false} />
        <span className="mt-1 block text-small text-(--color-faint)">
          Put {'{{mobile}}'}-style names where a value the bot collected should go.
          {placeholders.length > 0 && <> This address needs: <span className="font-mono text-(--color-ink)">{placeholders.join(', ')}</span>.</>}
        </span>
      </label>

      <label className="block">
        <span className="text-small font-medium text-(--color-muted)">How it signs in</span>
        <select value={form.authType} onChange={(e) => set({ authType: e.target.value })} className={FIELD}>
          <option value="none">It does not need a credential</option>
          <option value="bearer">Bearer token</option>
          <option value="apikey">API key in a header</option>
          <option value="basic">Username and password</option>
        </select>
      </label>
      {form.authType === 'apikey' && (
        <label className="block">
          <span className="text-small font-medium text-(--color-muted)">Header the key goes in</span>
          <input value={form.authHeader} onChange={(e) => set({ authHeader: e.target.value })} className={`${FIELD} font-mono`} placeholder="X-API-Key" />
        </label>
      )}
      {form.authType !== 'none' && (
        <label className={`block ${form.authType === 'apikey' ? 'sm:col-span-2' : ''}`}>
          <span className="text-small font-medium text-(--color-muted)">{form.authType === 'basic' ? 'user:password' : 'The credential'}</span>
          <input
            type="password"
            value={form.secret}
            onChange={(e) => set({ secret: e.target.value })}
            className={FIELD}
            autoComplete="new-password"
            placeholder={editing ? 'Leave blank to keep the stored one' : 'Stored encrypted; never shown again'}
          />
        </label>
      )}

      <details className="group sm:col-span-2 rounded-lg border border-(--color-line-dim)">
        <summary className="cursor-pointer list-none px-3 py-2 text-small font-medium text-(--color-muted) select-none">
          <span className="mr-1.5 inline-block transition group-open:rotate-90">▸</span>
          More{(form.description || form.headers.length) ? ' · set' : ''}
        </summary>
        <div className="grid gap-3 border-t border-(--color-line-dim) p-3">
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">A note for your team (optional)</span>
            <input value={form.description} onChange={(e) => set({ description: e.target.value })} className={FIELD} placeholder="Finds a customer in the CRM by mobile number" />
          </label>
          <div>
            <span className="text-small font-medium text-(--color-muted)">Headers sent every time (optional)</span>
            <div className="mt-1 flex flex-col gap-2">
              {form.headers.map(([k, v], i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <input value={k} onChange={(e) => set({ headers: form.headers.map((h, n) => (n === i ? [e.target.value, h[1]] : h)) })} placeholder="Accept" aria-label="Header" className={`${FIELD} mt-0 font-mono`} />
                  <input value={v} onChange={(e) => set({ headers: form.headers.map((h, n) => (n === i ? [h[0], e.target.value] : h)) })} placeholder="application/json" aria-label="Value" className={`${FIELD} mt-0 font-mono`} />
                  <button type="button" onClick={() => set({ headers: form.headers.filter((_, n) => n !== i) })} className={BUTTON} aria-label="Remove header">✕</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => set({ headers: [...form.headers, ['', '']] })} className={`${BUTTON} mt-2`}>Add a header</button>
          </div>
        </div>
      </details>

      <div className="flex gap-2 sm:col-span-2">
        <button type="submit" disabled={busy || !form.actionKey.trim() || !https} className={PRIMARY}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Save action'}
        </button>
        <button type="button" onClick={onCancel} className={`${BUTTON} px-3.5 py-2 text-body`}>Cancel</button>
      </div>
    </form>
  );
}

function safeConfig(json: string): { method?: string; url?: string; headers?: Record<string, string> } {
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as { method?: string; url?: string; headers?: Record<string, string> }) : {};
  } catch {
    return {};
  }
}
